import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trash2, Plus, MessageCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, API } from "@/lib/api";

const DEPTS = [["sala", "Sala"], ["bar", "Bar"], ["cucina", "Cucina"]];
const DEPT_LABEL = Object.fromEntries(DEPTS);
const inp = "bg-[#1E293B] border-slate-700 text-white h-8 text-sm";

export const ShiftsEditor = ({ q, save }) => {
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ dept: "sala", name: "", start: "", end: "" });
  const shifts = q.shifts || [];
  const notes = q.shift_notes || {};

  useEffect(() => { api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const addShift = () => {
    if (!form.name.trim()) return toast.error("Indica il nome di chi lavora");
    save({ shifts: [...shifts, { ...form, name: form.name.trim() }] });
    setForm({ ...form, name: "", start: "", end: "" });
  };

  const setNote = (dept, val) => save({ shift_notes: { ...notes, [dept]: val } });

  const sendWhatsApp = () => {
    const lines = [
      `*Programma evento — Bliss Café*`,
      `${q.event.type}${q.event.type_custom ? ` (${q.event.type_custom})` : ""} il ${q.event.date}, ${q.event.time_start}-${q.event.time_end}`,
      `${q.guests.adults + (q.guests.teens || 0) + q.guests.children} ospiti — preventivo n. ${q.number}`,
    ];
    DEPTS.forEach(([d, label]) => {
      const ds = shifts.filter((s) => s.dept === d);
      if (!ds.length && !notes[d]) return;
      lines.push(``, `*${label.toUpperCase()}*`);
      ds.forEach((s) => lines.push(`- ${s.name}${s.start ? ` ${s.start}` : ""}${s.end ? `-${s.end}` : ""}`));
      if (notes[d]) lines.push(`Nota: ${notes[d]}`);
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(lines.join("\n"))}`, "_blank");
  };

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-2">Turni personale</h3>
      <div className="bg-[#1E293B] rounded-lg p-3 space-y-3">
        {DEPTS.map(([d, label]) => {
          const ds = shifts.map((s, i) => ({ ...s, _i: i })).filter((s) => s.dept === d);
          return (
            <div key={d}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
              {ds.map((s) => (
                <div key={s._i} data-testid={`shift-row-${s._i}`} className="flex items-center gap-2 text-sm py-1 border-b border-slate-700/40">
                  <span className="font-semibold">{s.name}</span>
                  {(s.start || s.end) && <span className="font-mono-num text-xs text-slate-400">{s.start}{s.end ? `–${s.end}` : ""}</span>}
                  <button data-testid={`shift-delete-${s._i}`} onClick={() => save({ shifts: shifts.filter((_, j) => j !== s._i) })} className="ml-auto text-slate-500 hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <Input data-testid={`shift-note-${d}`} className={`${inp} mt-1.5`} placeholder={`Nota turno ${label} (opzionale)…`}
                defaultValue={notes[d] || ""} onBlur={(e) => e.target.value !== (notes[d] || "") && setNote(d, e.target.value)} />
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-700/60">
          <select data-testid="shift-dept-select" value={form.dept} onChange={(e) => setForm({ ...form, dept: e.target.value })}
            className="bg-[#1E293B] border border-slate-700 rounded-md h-8 text-sm px-2">
            {DEPTS.map(([d, label]) => <option key={d} value={d}>{label}</option>)}
          </select>
          <Input data-testid="shift-name-input" list="staff-names" className={`${inp} flex-1 min-w-[120px]`} placeholder="Nome…"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <datalist id="staff-names">
            {staff.map((s) => <option key={s.id} value={s.name}>{DEPT_LABEL[s.dept]}</option>)}
          </datalist>
          <Input data-testid="shift-start-input" type="time" className={`${inp} w-24`} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
          <Input data-testid="shift-end-input" type="time" className={`${inp} w-24`} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
          <Button data-testid="shift-add-button" size="sm" className="bg-[#C85A32] hover:bg-[#a94a28] h-8" onClick={addShift}><Plus className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center justify-end flex-wrap gap-2">
          <a data-testid="shifts-pdf-link" href={`${API}/quotes/${q.id}/turni-pdf`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" className="border-slate-600 text-slate-900 h-8"><FileText className="w-4 h-4 mr-1" /> PDF turni</Button>
          </a>
          <Button data-testid="shifts-whatsapp-button" size="sm" className="bg-[#25D366] hover:bg-[#1eb455] text-white h-8" onClick={sendWhatsApp}>
            <MessageCircle className="w-4 h-4 mr-1" /> Invia programma su WhatsApp
          </Button>
        </div>
      </div>
    </div>
  );
};
